import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { 
  Plus, Edit, Trash2, Calendar, Clock, X, Save, ChevronDown, ChevronUp
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminItineraries = () => {
  const { adminAuth, fetchStats } = useOutletContext();
  const [itineraries, setItineraries] = useState([]);
  const [destinations, setDestinations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [formData, setFormData] = useState({
    destination_id: '',
    name: '',
    duration: '',
    days: []
  });

  useEffect(() => {
    fetchItineraries();
    fetchDestinations();
  }, []);

  const fetchItineraries = async () => {
    try {
      const response = await fetch(`${API_URL}/api/itineraries`);
      const data = await response.json();
      setItineraries(data);
    } catch (error) {
      toast.error('Failed to fetch itineraries');
    } finally {
      setLoading(false);
    }
  };

  const fetchDestinations = async () => {
    try {
      const response = await fetch(`${API_URL}/api/destinations`);
      const data = await response.json();
      setDestinations(data);
    } catch (error) {
      console.error('Failed to fetch destinations');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const method = editingId ? 'PUT' : 'POST';
      const url = editingId 
        ? `${API_URL}/api/admin/itineraries/${editingId}`
        : `${API_URL}/api/admin/itineraries`;
      
      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Basic ${adminAuth}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success(editingId ? 'Itinerary updated!' : 'Itinerary created!');
        resetForm();
        fetchItineraries();
        fetchStats();
      } else {
        toast.error('Operation failed');
      }
    } catch (error) {
      toast.error('Connection error');
    }
  };

  const handleEdit = (itinerary) => {
    setFormData({
      destination_id: itinerary.destination_id,
      name: itinerary.name,
      duration: itinerary.duration,
      days: itinerary.days || []
    });
    setEditingId(itinerary.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this itinerary?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/admin/itineraries/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Basic ${adminAuth}`
        }
      });

      if (response.ok) {
        toast.success('Itinerary deleted');
        fetchItineraries();
        fetchStats();
      } else {
        toast.error('Delete failed');
      }
    } catch (error) {
      toast.error('Connection error');
    }
  };

  const resetForm = () => {
    setFormData({
      destination_id: '',
      name: '',
      duration: '',
      days: []
    });
    setEditingId(null);
    setShowForm(false);
  };

  const addDay = () => {
    const newDay = {
      day: formData.days.length + 1,
      title: `Day ${formData.days.length + 1}`,
      activities: []
    };
    setFormData({
      ...formData,
      days: [...formData.days, newDay]
    });
  };

  const updateDay = (index, field, value) => {
    const newDays = [...formData.days];
    newDays[index][field] = value;
    setFormData({ ...formData, days: newDays });
  };

  const updateDayActivities = (index, value) => {
    const activities = value.split(',').map(a => a.trim()).filter(a => a);
    const newDays = [...formData.days];
    newDays[index].activities = activities;
    setFormData({ ...formData, days: newDays });
  };

  const removeDay = (index) => {
    const newDays = formData.days.filter((_, i) => i !== index).map((day, i) => ({
      ...day,
      day: i + 1
    }));
    setFormData({ ...formData, days: newDays });
  };

  const getDestinationName = (id) => {
    const dest = destinations.find(d => d.id === id);
    return dest?.name || 'Unknown';
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Itineraries</h1>
          <p className="text-gray-600">Manage pre-built trip packages</p>
        </div>
        <Button 
          onClick={() => setShowForm(true)} 
          className="bg-amber-600 hover:bg-amber-700"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Itinerary
        </Button>
      </div>

      {/* Itinerary Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>{editingId ? 'Edit Itinerary' : 'Add Itinerary'}</CardTitle>
              <Button variant="ghost" size="sm" onClick={resetForm}>
                <X className="w-5 h-5" />
              </Button>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Destination</label>
                    <select
                      value={formData.destination_id}
                      onChange={(e) => setFormData({ ...formData, destination_id: e.target.value })}
                      className="w-full p-2 border rounded-md"
                      required
                    >
                      <option value="">Select destination</option>
                      {destinations.map(dest => (
                        <option key={dest.id} value={dest.id}>{dest.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Duration</label>
                    <Input
                      value={formData.duration}
                      onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                      placeholder="e.g., 5 Days"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Itinerary Name</label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Classic Bali Experience"
                    required
                  />
                </div>

                {/* Days Section */}
                <div className="border-t pt-4">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="font-semibold">Daily Schedule</h3>
                    <Button type="button" variant="outline" size="sm" onClick={addDay}>
                      <Plus className="w-4 h-4 mr-1" /> Add Day
                    </Button>
                  </div>
                  
                  <div className="space-y-3 max-h-60 overflow-y-auto">
                    {formData.days.map((day, index) => (
                      <div key={index} className="bg-gray-50 p-3 rounded-lg">
                        <div className="flex justify-between items-start mb-2">
                          <span className="text-sm font-medium text-amber-600">Day {day.day}</span>
                          <button 
                            type="button" 
                            onClick={() => removeDay(index)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                        <Input
                          value={day.title}
                          onChange={(e) => updateDay(index, 'title', e.target.value)}
                          placeholder="Day title (e.g., Arrival & Beach Day)"
                          className="text-sm mb-2"
                        />
                        <Input
                          value={day.activities.join(', ')}
                          onChange={(e) => updateDayActivities(index, e.target.value)}
                          placeholder="Activities (comma separated)"
                          className="text-sm"
                        />
                      </div>
                    ))}
                    {formData.days.length === 0 && (
                      <p className="text-gray-500 text-sm text-center py-4">
                        No days added yet. Click &quot;Add Day&quot; to start building the itinerary.
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  <Button type="submit" className="bg-amber-600 hover:bg-amber-700">
                    <Save className="w-4 h-4 mr-2" />
                    {editingId ? 'Update' : 'Create'}
                  </Button>
                  <Button type="button" variant="outline" onClick={resetForm}>
                    Cancel
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Itineraries List */}
      <div className="space-y-4">
        {itineraries.map((itinerary) => (
          <Card key={itinerary.id} className="overflow-hidden">
            <CardContent className="p-0">
              <div 
                className="p-4 cursor-pointer hover:bg-gray-50 flex items-center justify-between"
                onClick={() => setExpandedId(expandedId === itinerary.id ? null : itinerary.id)}
              >
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-amber-100 rounded-lg">
                    <Calendar className="w-6 h-6 text-amber-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg">{itinerary.name}</h3>
                    <div className="flex items-center gap-4 text-sm text-gray-600">
                      <span>{getDestinationName(itinerary.destination_id)}</span>
                      <span className="flex items-center">
                        <Clock className="w-4 h-4 mr-1" />
                        {itinerary.duration}
                      </span>
                      <span>{itinerary.days?.length || 0} days</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={(e) => { e.stopPropagation(); handleEdit(itinerary); }}
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-red-600"
                    onClick={(e) => { e.stopPropagation(); handleDelete(itinerary.id); }}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                  {expandedId === itinerary.id ? (
                    <ChevronUp className="w-5 h-5 text-gray-400" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400" />
                  )}
                </div>
              </div>

              {/* Expanded Day Details */}
              {expandedId === itinerary.id && (
                <div className="border-t bg-gray-50 p-4">
                  <div className="space-y-3">
                    {itinerary.days?.map((day, idx) => (
                      <div key={idx} className="flex gap-4">
                        <div className="w-20 shrink-0">
                          <span className="inline-block bg-amber-600 text-white text-xs px-2 py-1 rounded">
                            Day {day.day}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium">{day.title}</p>
                          <p className="text-sm text-gray-600">
                            {day.activities?.join(' → ')}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        ))}

        {itineraries.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            <Calendar className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No itineraries yet. Click &quot;Add Itinerary&quot; to create one.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminItineraries;
